import React from 'react';
import {
    NpvSensitivityTable,
    CapitalIrrSensitivityTable,
    ProjectIrrSensitivityTable,
    LandCostSensitivityTable,
    BessArbitrageSpreadSensitivityTable,
    BessAncillaryCapitalCostSensitivityTable,
    BessEfficiencyReplacementSensitivityTable,
    BessReplacementPeriodSensitivityTable,
    BessTariffArbitrageSensitivityTable,
    BessSizingOptimizationTable
} from './sensitivity';

const SensitivityAnalysisTab = ({ outputs, inputs }) => (
    <div>
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Sensitivity Analysis</h2>
        <div className="space-y-6">
            
            {/* Core Sensitivity Tables */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">📊 Core Financial Sensitivities</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    These tables show how key financial metrics respond to changes in critical input parameters.
                </p>
                
                <NpvSensitivityTable inputs={inputs} />
                <CapitalIrrSensitivityTable inputs={inputs} />
                <ProjectIrrSensitivityTable inputs={inputs} />
                <LandCostSensitivityTable inputs={inputs} />
            </div>

            {/* BESS Sensitivity Analysis - Only shown when BESS is enabled */}
            {inputs.bessEnabled && (
                <div className="bg-purple-50 dark:bg-purple-900/20 p-6 rounded-lg border border-purple-200 dark:border-purple-700">
                    <h3 className="text-lg font-bold text-purple-800 dark:text-purple-200 mb-4">🔋 BESS Sensitivity Analysis</h3>
                    <p className="text-sm text-purple-700 dark:text-purple-300 mb-4">
                        Battery energy storage systems introduce additional risk factors that require specialized sensitivity analysis. 
                        These tables help evaluate critical BESS investment decisions and risk exposure.
                    </p>
                    
                    <BessArbitrageSpreadSensitivityTable inputs={inputs} />
                    <BessAncillaryCapitalCostSensitivityTable inputs={inputs} />
                    <BessEfficiencyReplacementSensitivityTable inputs={inputs} />
                    <BessReplacementPeriodSensitivityTable inputs={inputs} />
                    <BessTariffArbitrageSensitivityTable inputs={inputs} />
                    <BessSizingOptimizationTable inputs={inputs} />
                </div>
            )}

            {/* BESS Decision Framework */}
            {inputs.bessEnabled && (
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">🎯 BESS Decision Framework</h3>
                    
                    <div className="mt-4">
                        <h4 className="font-semibold text-blue-700 dark:text-blue-300 mb-2">1. High-Impact Revenue Sensitivities</h4>
                        <div className="ml-4 space-y-2 text-sm">
                            <div>
                                <strong className="text-purple-600 dark:text-purple-400">A. Arbitrage Spread vs BESS Degradation Rate</strong>
                                <p><strong>Why Critical:</strong> Arbitrage spread is the most volatile revenue stream (market-dependent), and degradation affects all energy-based revenues</p>
                                <p><strong>Ranges:</strong> Arbitrage spread ±50% (-20% to +30%), Degradation rate ±40% (1.0% to 2.5%/year)</p>
                                <p><strong>Output Metric:</strong> Project NPV or BESS NPV contribution</p>
                            </div>
                            <div>
                                <strong className="text-purple-600 dark:text-purple-400">B. Ancillary Services Rate vs BESS Capital Cost ($/MWh)</strong>
                                <p><strong>Why Critical:</strong> Ancillary rates are regulatory/policy dependent, and battery costs are declining rapidly but uncertain</p>
                                <p><strong>Ranges:</strong> Ancillary rates ±40%, Capital costs ±30%</p>
                                <p><strong>Output Metric:</strong> BESS Payback Period</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="mt-4">
                        <h4 className="font-semibold text-blue-700 dark:text-blue-300 mb-2">2. Technology Risk Sensitivities</h4>
                        <div className="ml-4 space-y-2 text-sm">
                            <div>
                                <strong className="text-purple-600 dark:text-purple-400">C. Round-trip Efficiency vs Replacement Cost Factor</strong>
                                <p><strong>Why Critical:</strong> Efficiency affects all energy revenues; replacement costs depend on technology learning curves</p>
                                <p><strong>Ranges:</strong> Efficiency ±10% (80%-95%), Replacement factor ±30% (40%-70% of original cost)</p>
                                <p><strong>Output Metric:</strong> Total BESS Cash Flow</p>
                            </div>
                            <div>
                                <strong className="text-purple-600 dark:text-purple-400">D. Replacement Period vs Battery Degradation Rate</strong>
                                <p><strong>Why Critical:</strong> Both affect replacement timing and long-term economics</p>
                                <p><strong>Ranges:</strong> Replacement period ±33% (10-20 years), Degradation ±40%</p>
                                <p><strong>Output Metric:</strong> NPV of BESS over project life</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="mt-4">
                        <h4 className="font-semibold text-blue-700 dark:text-blue-300 mb-2">3. Market/Financial Sensitivities</h4>
                        <div className="ml-4 space-y-2 text-sm">
                            <div>
                                <strong className="text-purple-600 dark:text-purple-400">E. Grid Tariff vs Arbitrage Spread</strong>
                                <p><strong>Why Critical:</strong> Shows how BESS value changes with overall electricity prices</p>
                                <p><strong>Ranges:</strong> Grid tariff ±20%, Arbitrage spread ±50%</p>
                                <p><strong>Output Metric:</strong> BESS Revenue Enhancement vs Solar-only</p>
                            </div>
                            <div>
                                <strong className="text-purple-600 dark:text-purple-400">F. BESS Energy Capacity vs Arbitrage Cycles/Day</strong>
                                <p><strong>Why Critical:</strong> Sizing optimization - bigger isn't always better</p>
                                <p><strong>Ranges:</strong> Energy capacity ±40%, Cycles ±50% (0.8-2.0 cycles/day)</p>
                                <p><strong>Output Metric:</strong> BESS Revenue per $ Invested</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="mt-4">
                        <h4 className="font-semibold text-green-700 dark:text-green-300 mb-2">🎯 Key Questions Answered</h4>
                        <p className="text-sm">The sensitivity analysis should help answer:</p>
                        <div className="ml-4 space-y-1 text-sm">
                            <p><strong>Threshold Analysis:</strong> At what arbitrage spread does BESS become uneconomical?</p>
                            <p><strong>Break-even Points:</strong> How high can degradation rates go before replacement economics fail?</p>
                            <p><strong>Sizing Optimization:</strong> What's the optimal energy/power ratio for different market conditions?</p>
                            <p><strong>Technology Risk:</strong> How sensitive is the project to battery technology assumptions?</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    </div>
);

export default SensitivityAnalysisTab; 